import clsx from 'clsx';
import { graphql } from 'gatsby';
import { twMerge } from 'tailwind-merge';

import { SEO } from 'components';
import { Col, Container, Row, Section } from 'components/GridV2';
import { ArticleCards } from 'components/LearnArticleCards';
import { Heading3 } from 'components/Typography';
import { AboutTheAuthor } from 'components/pages/author';
import LearnBreadCrumb from 'components/pages/author/LearnBreadcrumb';
import { Layout } from 'components/rebrand/Layout';

import { TAfAdsets } from 'typescriptDefs/afTracking';
import { IAuthorProps } from 'typescriptDefs/pages/learn/author';

import { useGtagRemarketing } from 'helpers';

const Author = ({
  data: { author, authorPosts },
  pageContext: { linkCanonical, locale, pageIsMultilingual, pageName, schemaMarkupJson },
  ...props
}: IAuthorProps) => {
  useGtagRemarketing('JOxeCPXq4JYDEI-zyK4D');
  const hasValidAuthorData = !!author.img && !!author.socialLinks && !!author.bio;

  const copy = {
    meta: {
      title: {
        en: `${author.name} | KOHO Author Profile`,
        fr: `${author.name} | Profil de l'auteur KOHO`,
      }[locale],
      description: {
        en: `We've created a space to make managing your money a little less daunting. Consider it the personal finance course you never learned in school. Learn how to make a budget, how to tackle your debt, how to avoid bankruptcy, how to save money, and how to invest from financial management experts.`,
        fr: `Nous avons créé un espace pour rendre la gestion de votre argent un peu moins intimidante. Considérez-le comme le cours de finances personnelles que vous n'avez jamais appris à l'école. Apprenez à faire un budget, à gérer vos dettes, à éviter la faillite, à épargner et à investir grâce à des experts en gestion financière.`,
      }[locale],
      keywords: {
        en: `personal finance, financial author, financial advice, financial literacy`,
        fr: `finances personnelles, auteur financier, conseils financiers, éducation financière`,
      }[locale],
    },
  };

  return (
    <Layout
      afAdset={author._id.split('-')[0] as TAfAdsets}
      location={props.location}
      pageIsMultilingual={pageIsMultilingual}
      pageName={pageName}
      pageVariant={author.name}
      oldTheme
    >
      <SEO
        canonical={linkCanonical}
        description={author.bio ? author.bio : copy.meta.description}
        imageSrc={``}
        keywords={copy.meta.keywords}
        location={props.location}
        multilingual={pageIsMultilingual}
        title={copy.meta.title}
        structuredData={[
          {
            '@context': 'https://schema.org/',
            '@type': 'Person',
            name: author.name,
            url: `https://www.koho.ca${{ en: '/', fr: '/fr/' }[locale]}author/${author.slug.current}/`,
            ...(author.img ? { image: author.img.asset.url } : {}),
            ...(author.bio ? { description: author.bio } : {}),
            ...(author.socialLinks && author.socialLinks.length > 0
              ? { sameAs: author.socialLinks?.map((link) => link.href) }
              : {}),
            ...(author.expertise && author.expertise.length > 0 ? { knowsAbout: author.expertise } : {}),
            ...(author.education && author.education.length > 0
              ? {
                  alumniOf: author.education.map((text) => ({
                    '@type': 'Organization',
                    Name: text,
                  })),
                }
              : {}),
          },
          ...(schemaMarkupJson || []),
        ]}
      />

      {hasValidAuthorData && <AboutTheAuthor author={author} />}

      <Section className={twMerge(clsx('pb-32 pt-84 md:pb-64 md:pt-96', hasValidAuthorData && 'pt-32 md:pt-64'))}>
        {!hasValidAuthorData && <LearnBreadCrumb />}
        <Container className="px-30 xs:px-0">
          <Row className="mb-16 flex-wrap px-0 md:mb-32 md:px-80">
            <Col className="flex-none">
              <Heading3 as="h1" className="mb-4 mr-0 block xs:mb-0 xs:mr-8 xs:inline-block">
                {{ en: `Blog posts by`, fr: `Blogs publiés par` }[locale]}
              </Heading3>
              <Heading3 as="h2" className="block xs:inline-block">
                {author.name}
              </Heading3>
            </Col>
          </Row>
          <ArticleCards articles={authorPosts.nodes} />
        </Container>
      </Section>
    </Layout>
  );
};

export default Author;

export const queryGraphQL = graphql`
  query ($_id: String!, $locale: String!) {
    author: sanityBlogAuthor(_id: { eq: $_id }) {
      _id
      bio
      education
      expertise
      img {
        asset {
          gatsbyImageData(placeholder: NONE, width: 200, height: 200)
          url
        }
      }
      locale
      name
      socialLinks {
        href
        icon
      }
      slug {
        current
      }
    }
    authorPosts: allSanityBlogArticle(
      filter: { author: { _id: { eq: $_id } }, locale: { eq: $locale } }
      sort: { dateTimePublished: DESC }
      limit: 6
    ) {
      nodes {
        _rawExcerpt
        authorData: author {
          locale
          name
          slug {
            current
          }
        }
        img {
          asset {
            gatsbyImageData(placeholder: NONE, width: 576)
          }
        }
        imgAltText
        minutesToRead
        slug {
          current
        }
        subfolder
        tags
        title
      }
    }
  }
`;
